/**
 * Funnel analysis page.
 *
 * A step builder (ordered event-name or URL-path steps) plus a bar rendering of
 * the result from POST /api/revenue/funnels. The semantics footnote mirrors the
 * module docstring in packages/revenue/src/funnels.ts — they must not drift.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  api,
  ApiFailure,
  type FunnelResultData,
  type FunnelStepInput,
  type RevenueWebsite,
} from '../api';
import { Notice } from './Panels';

interface DraftStep {
  kind: 'event' | 'url';
  value: string;
}

const DEFAULT_STEPS: DraftStep[] = [
  { kind: 'url', value: '/' },
  { kind: 'event', value: 'signup' },
];

function formatPercent(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(1)}%`;
}

function formatDuration(ms: number): string {
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

function stepLabel(step: FunnelStepInput): string {
  return step.kind === 'event' ? `Event: ${step.name}` : `URL: ${step.path}`;
}

export function Funnels() {
  const [websites, setWebsites] = useState<RevenueWebsite[]>([]);
  const [websiteId, setWebsiteId] = useState<string>('');
  const [dateRange, setDateRange] = useState('30d');
  const [steps, setSteps] = useState<DraftStep[]>(DEFAULT_STEPS);
  const [result, setResult] = useState<FunnelResultData | null>(null);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  useEffect(() => {
    api.listRevenueWebsites()
      .then((data) => setWebsites(data.websites))
      .catch((error: unknown) => {
        setFailure(error instanceof ApiFailure ? error : new ApiFailure('Request failed.', 'Try again.', 0));
      });
  }, []);

  const run = useCallback(async (draft: DraftStep[], website: string, range: string) => {
    const valid = draft.filter((s) => s.value.trim() !== '');
    if (valid.length < 2) {
      setResult(null);
      return;
    }
    setLoading(true);
    setFailure(null);
    try {
      const endDate = new Date().toISOString().slice(0, 10);
      const days = range === '7d' ? 7 : range === '30d' ? 30 : range === '90d' ? 90 : 365;
      const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const response = await api.runRevenueFunnel({
        steps: valid.map((s) =>
          s.kind === 'event' ? { kind: 'event' as const, name: s.value.trim() } : { kind: 'url' as const, path: s.value.trim() },
        ),
        startDate,
        endDate,
        ...(website !== '' ? { websiteId: website } : {}),
      });
      setResult(response.funnel);
    } catch (error) {
      setResult(null);
      setFailure(error instanceof ApiFailure ? error : new ApiFailure('Request failed.', 'Try again.', 0));
    } finally {
      setLoading(false);
    }
  }, []);

  // Run once on load with the default funnel; step edits wait for "Run funnel"
  // so typing does not fire a request per keystroke.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void run(DEFAULT_STEPS, '', '30d'); }, [run]);

  function updateStep(index: number, patch: Partial<DraftStep>): void {
    setSteps(steps.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }

  function moveStep(index: number, direction: -1 | 1): void {
    const target = index + direction;
    if (target < 0 || target >= steps.length) return;
    const next = [...steps];
    [next[index], next[target]] = [next[target]!, next[index]!];
    setSteps(next);
  }

  if (websites.length === 0 && failure === null) {
    return (
      <div className="page">
        <div className="card empty">
          <h3>No websites configured</h3>
          <p>
            Funnels are computed from tracked events. Create a website and install its snippet first — go to Websites
            in the sidebar — or import an uploaded CSV on the Revenue page; the import creates a csv-import.local
            website whose events you can funnel here.
          </p>
        </div>
      </div>
    );
  }

  const filledSteps = steps.filter((s) => s.value.trim() !== '').length;
  const maxVisitors = result !== null ? Math.max(...result.steps.map((s) => s.visitors), 0) : 0;

  return (
    <div className="page">
      <header className="intro">
        <h2>Funnels</h2>
        <p>How many visitors progress through an ordered sequence of steps, and how long each transition takes.</p>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      <section className="card">
        <header><h2>Steps</h2></header>
        {steps.map((step, index) => (
          <div className="row tight" key={index} style={{ marginBottom: 8, alignItems: 'center' }}>
            <span className="muted" style={{ minWidth: 20, textAlign: 'right' }}>{index + 1}.</span>
            <select
              value={step.kind}
              aria-label={`Step ${index + 1} type`}
              onChange={(e) => updateStep(index, { kind: e.target.value as 'event' | 'url' })}
            >
              <option value="event">Event name</option>
              <option value="url">URL path</option>
            </select>
            <input
              type="text"
              value={step.value}
              placeholder={step.kind === 'event' ? 'e.g. signup' : 'e.g. /dashboard'}
              aria-label={`Step ${index + 1} ${step.kind === 'event' ? 'event name' : 'URL path'}`}
              onChange={(e) => updateStep(index, { value: e.target.value })}
              style={{ flex: 1 }}
            />
            <button className="ghost tiny" onClick={() => moveStep(index, -1)} disabled={index === 0} aria-label={`Move step ${index + 1} up`}>↑</button>
            <button className="ghost tiny" onClick={() => moveStep(index, 1)} disabled={index === steps.length - 1} aria-label={`Move step ${index + 1} down`}>↓</button>
            <button
              className="ghost tiny"
              onClick={() => setSteps(steps.filter((_, i) => i !== index))}
              disabled={steps.length <= 2}
              aria-label={`Remove step ${index + 1}`}
            >
              ✕
            </button>
          </div>
        ))}
        <div className="row tight" style={{ alignItems: 'center' }}>
          <button
            className="ghost"
            onClick={() => setSteps([...steps, { kind: 'event', value: '' }])}
            disabled={steps.length >= 8}
          >
            + Add step
          </button>
          <span style={{ flex: 1 }} />
          <label className="field">
            <span>Website</span>
            <select value={websiteId} onChange={(e) => setWebsiteId(e.target.value)}>
              <option value="">All websites</option>
              {websites.map((w) => (
                <option key={w.id} value={w.id}>{w.domain}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Period</span>
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="12m">Last 12 months</option>
            </select>
          </label>
          <button
            className="primary"
            onClick={() => void run(steps, websiteId, dateRange)}
            disabled={loading || filledSteps < 2}
          >
            Run funnel
          </button>
        </div>
        {filledSteps < 2 && (
          <p className="muted">A funnel needs at least two steps with a value.</p>
        )}
      </section>

      {loading && <div className="card"><p className="muted"><span className="spinner" /> Computing funnel…</p></div>}

      {!loading && result !== null && (
        <section className="card">
          <header>
            <h2>Results</h2>
            <p className="muted" style={{ margin: 0 }}>
              {result.eventsConsidered.toLocaleString('en-US')} events · {result.visitorsInWindow.toLocaleString('en-US')} unique visitors in window
            </p>
          </header>

          {result.steps[0] !== undefined && result.steps[0].visitors === 0 ? (
            <div className="card empty">
              <h3>No visitors reached step 1</h3>
              <p>No events in this window match “{stepLabel(result.steps[0].step)}”. Widen the period or check the step definition.</p>
            </div>
          ) : (
            <div role="list" aria-label="Funnel steps">
              {result.steps.map((step, index) => (
                <div key={index} role="listitem" style={{ marginBottom: 4 }}>
                  <div className="row tight" style={{ justifyContent: 'space-between', marginBottom: 2 }}>
                    <span style={{ fontWeight: 500 }}>{index + 1}. {stepLabel(step.step)}</span>
                    <span className="muted">
                      {step.visitors.toLocaleString('en-US')} visitors
                      {step.overallConversion !== null && index > 0 && ` · ${formatPercent(step.overallConversion)} overall`}
                    </span>
                  </div>
                  <div
                    role="img"
                    aria-label={`${stepLabel(step.step)}: ${step.visitors} visitors${step.overallConversion !== null ? `, ${formatPercent(step.overallConversion)} of step 1` : ''}`}
                    style={{
                      height: 22,
                      borderRadius: 4,
                      width: `${maxVisitors === 0 ? 0 : Math.max((step.visitors / maxVisitors) * 100, step.visitors > 0 ? 1.5 : 0)}%`,
                      background: 'color-mix(in srgb, var(--series-1) 80%, var(--surface))',
                      transition: 'width 200ms ease',
                    }}
                  />
                  {index > 0 && (
                    <div className="row tight" style={{ marginTop: 2, gap: 8 }}>
                      <span className={step.visitors > 0 ? 'badge accent' : 'badge'}>
                        {step.visitors > 0
                          ? `↓ ${formatPercent(step.conversionFromPrevious)} from step ${index}`
                          : `No events match step ${index + 1} after step ${index}`}
                      </span>
                      {step.medianTimeFromPreviousMs !== null && (
                        <span className="badge">median {formatDuration(step.medianTimeFromPreviousMs)} from step {index}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <p className="muted" style={{ marginTop: 16, marginBottom: 0, fontSize: 12 }}>
            A step matches an event name (case-insensitive) or an exact URL path. Steps must be reached in order —
            one event satisfies at most one step, and a visitor who skips or reorders steps is not counted further.
            Only events inside the selected period count. Percentages are relative to the previous step and to step 1;
            “median” is the middle visitor's time between those steps.
          </p>
        </section>
      )}
    </div>
  );
}
